import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { licenseAPI } from '../services/api';
import { Copy, Check, AlertCircle } from 'lucide-react';

const SetupPage = () => {
  const navigate = useNavigate();
  const { license } = useAuth();
  const { isDark } = useTheme();
  const [vpnScript, setVpnScript] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVPNScript();
  }, []);

  const loadVPNScript = async () => {
    try {
      const response = await licenseAPI.getVPNScript();
      setVpnScript(response.data);
    } catch (error) {
      console.error('Error loading VPN script:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(vpnScript.script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-purple-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-12 px-4 transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 to-purple-50'
    }`}>
      <div className="max-w-4xl mx-auto">
        <div className={`rounded-2xl shadow-xl p-8 mb-6 transition-colors duration-300 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              ¡Bienvenido a EasyTickets!
            </h1>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Tu cuenta ha sido creada exitosamente. Ahora configura tu VPN.
            </p>
          </div>

          {/* Alerta importante */}
          <div className={`border-l-4 border-yellow-400 p-4 mb-8 ${
            isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'
          }`}>
            <div className="flex">
              <AlertCircle className="w-6 h-6 text-yellow-400 mr-3" />
              <div>
                <h3 className={`text-sm font-medium mb-1 ${
                  isDark ? 'text-yellow-300' : 'text-yellow-800'
                }`}>
                  Importante: Tienes 3 días para conectar
                </h3>
                <p className={`text-sm ${
                  isDark ? 'text-yellow-200' : 'text-yellow-700'
                }`}>
                  Debes conectar tu Mikrotik usando el script a continuación en los próximos 3 días, o tu cuenta será eliminada automáticamente.
                </p>
              </div>
            </div>
          </div>

          {/* Credenciales VPN */}
          <div className={`rounded-lg p-6 mb-8 ${
            isDark ? 'bg-blue-900/20' : 'bg-blue-50'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Tus Credenciales VPN
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Servidor VPN:</span>
                <p className={`font-mono font-semibold ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                  {vpnScript?.vpn_server}
                </p>
              </div>
              <div>
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Usuario:</span>
                <p className={`font-mono font-semibold ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                  {vpnScript?.username}
                </p>
              </div>
              <div>
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Contraseña:</span>
                <p className={`font-mono font-semibold ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                  {vpnScript?.password}
                </p>
              </div>
              <div>
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>IP Interna:</span>
                <p className={`font-mono font-semibold ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                  {vpnScript?.internal_ip}
                </p>
              </div>
            </div>
          </div>

          {/* Script para copiar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-3">
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Script de Configuración
              </h2>
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copiar Script
                  </>
                )}
              </button>
            </div>
            <div className={`rounded-lg p-4 font-mono text-sm overflow-x-auto ${
              isDark 
                ? 'bg-gray-900 text-green-400' 
                : 'bg-gray-900 text-green-400'
            }`}>
              <pre>{vpnScript?.script}</pre>
            </div>
          </div>

          {/* Instrucciones */}
          <div className={`rounded-lg p-6 mb-8 ${
            isDark ? 'bg-gray-700/50' : 'bg-gray-50'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Instrucciones
            </h2>
            <ol className="space-y-3">
              {vpnScript?.instructions.map((instruction, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </span>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {instruction}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Botón continuar */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Ir al Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupPage;
